<template>
  <div
    class="esport-match-card-view"
    :data-match-id="match.id"
    :class="[
      variant,
      {
        done: match.status === 'selesai',
        bye: match.status === 'bye',
        placeholder: match.isPlaceholder,
        live: isLive,
      },
    ]"
  >
    <div class="match-number">
      <span>PES</span>
      <span v-if="isLive" class="live-tag"><span class="live-dot"></span> LIVE</span>
      <span v-else-if="countdownText" class="soon-tag"
        ><v-icon x-small>mdi-timer-outline</v-icon>{{ countdownText }}</span
      >
    </div>
    <div class="match-row" :class="{ winner: isWinner(match.peserta1_id) }">
      <div class="player-info">
        <span v-if="isWinner(match.peserta1_id)" class="winner-icon"
          ><v-icon x-small>mdi-crown</v-icon></span
        ><span class="match-nama">{{ nama1 }}</span>
      </div>
      <span v-if="showSkor" class="skor-box">{{ skorTampil(match.skor1) }}</span>
      <span v-else-if="match.status === 'bye'" class="bye-tag">BYE</span
      ><span v-else-if="match.isPlaceholder" class="tbd-tag">TBD</span
      ><span v-else class="vs-dash">—</span>
    </div>
    <div class="player-divider"></div>
    <div class="match-row" :class="{ winner: isWinner(match.peserta2_id) }">
      <div class="player-info">
        <span v-if="isWinner(match.peserta2_id)" class="winner-icon"
          ><v-icon x-small>mdi-crown</v-icon></span
        ><span class="match-nama">{{
          match.isPlaceholder || match.peserta2_id ? nama2 : "—"
        }}</span>
      </div>
      <span v-if="showSkor" class="skor-box">{{ skorTampil(match.skor2) }}</span
      ><span v-else-if="match.isPlaceholder" class="tbd-tag">TBD</span
      ><span v-else-if="match.status === 'menunggu'" class="vs-dash">—</span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from "vue";
const props = defineProps({
  match: { type: Object, required: true },
  nama1: { type: String, default: "—" },
  nama2: { type: String, default: "—" },
  variant: { type: String, default: "normal" },
});
const adaSkor = (v) => v !== null && v !== undefined && v !== "";
const showSkor = computed(
  () =>
    !props.match.isPlaceholder &&
    props.match.status !== "bye" &&
    adaSkor(props.match.skor1) &&
    adaSkor(props.match.skor2)
);
const skorTampil = (v) => (v === null || v === undefined || v === "" ? "0" : v);
const isWinner = (id) =>
  !props.match.isPlaceholder &&
  !!props.match.pemenang_id &&
  !!id &&
  props.match.pemenang_id === id;
const now = ref(Date.now());
let timer = null;
const jadwalMulaiMs = computed(() => {
  if (!props.match.jadwal_mulai) return null;
  const t = new Date(String(props.match.jadwal_mulai).replace(" ", "T")).getTime();
  return Number.isNaN(t) ? null : t;
});
const msRemaining = computed(() =>
  jadwalMulaiMs.value === null ? null : jadwalMulaiMs.value - now.value
);
const sudahDijadwalkan = computed(
  () =>
    !props.match.isPlaceholder &&
    props.match.status === "menunggu" &&
    !!props.match.peserta1_id &&
    !!props.match.peserta2_id &&
    jadwalMulaiMs.value !== null
);
const belumWaktunya = computed(() => sudahDijadwalkan.value && msRemaining.value > 0);
const formatCountdown = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000)),
    h = Math.floor(total / 3600),
    m = Math.floor((total % 3600) / 60),
    s = total % 60,
    p = (n) => String(n).padStart(2, "0");
  return h ? `${h}:${p(m)}:${p(s)}` : `${p(m)}:${p(s)}`;
};
const countdownText = computed(() =>
  belumWaktunya.value ? `dalam ${formatCountdown(msRemaining.value)}` : ""
);
const isLive = computed(
  () =>
    !props.match.isPlaceholder &&
    props.match.status === "menunggu" &&
    !!props.match.peserta1_id &&
    !!props.match.peserta2_id &&
    jadwalMulaiMs.value !== null &&
    msRemaining.value <= 0
);
const syncTimer = () => {
  const need = sudahDijadwalkan.value;
  if (need && !timer) timer = setInterval(() => (now.value = Date.now()), 1000);
  else if (!need && timer) {
    clearInterval(timer);
    timer = null;
  }
};
watch(
  () => props.match.jadwal_mulai,
  () => {
    now.value = Date.now();
    syncTimer();
  }
);
onMounted(syncTimer);
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
.esport-match-card-view {
  --teal: #028c73;
  --teal-dark: #016653;
  position: relative;
  width: 250px;
  min-height: 112px;
  padding: 10px;
  background: #ffffff;
  border: 1.5px solid #a8cec6;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(10, 60, 50, 0.13), 0 1px 3px rgba(10, 60, 50, 0.08);
  overflow: hidden;
  transition: 0.2s;
}
.esport-match-card-view:before {
  content: "";
  position: absolute;
  top: 0;
  left: 14px;
  right: 14px;
  height: 2.5px;
  background: linear-gradient(90deg, transparent, rgba(2, 140, 115, 0.7), transparent);
}
.esport-match-card-view:hover {
  transform: translateY(-2px);
  border-color: rgba(2, 140, 115, 0.6);
  box-shadow: 0 12px 28px rgba(10, 60, 50, 0.17);
}
.esport-match-card-view.small {
  width: 220px;
  min-height: 98px;
}
.esport-match-card-view.final {
  width: 270px;
  min-height: 118px;
  border: 2px solid rgba(183, 125, 22, 0.55);
  background: linear-gradient(145deg, #fffcf2, #fff);
  box-shadow: 0 14px 36px rgba(160, 110, 15, 0.18);
}
.esport-match-card-view.final:before {
  background: linear-gradient(90deg, transparent, rgba(200, 145, 30, 0.85), transparent);
}
.esport-match-card-view.done {
  border-color: rgba(2, 140, 115, 0.5);
  background: linear-gradient(145deg, rgba(2, 140, 115, 0.07), #fff);
}
.esport-match-card-view.bye {
  opacity: 0.74;
  background: #f8fbfa;
}
.esport-match-card-view.placeholder {
  opacity: 0.62;
  border-style: dashed;
  background: repeating-linear-gradient(
    135deg,
    #f8fbfa,
    #f8fbfa 9px,
    #f0f6f4 9px,
    #f0f6f4 18px
  );
}
.esport-match-card-view.live {
  border-color: rgba(225, 74, 74, 0.65);
  box-shadow: 0 0 0 3px rgba(225, 74, 74, 0.07), 0 12px 28px rgba(225, 74, 74, 0.1);
}
.esport-match-card-view.live:before {
  background: linear-gradient(90deg, transparent, rgba(225, 74, 74, 0.75), transparent);
}
.match-number {
  height: 19px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5px;
  font-size: 8.5px;
  font-weight: 900;
  color: #5a7870;
  text-transform: uppercase;
  letter-spacing: 0.65px;
}
.live-tag,
.soon-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 7px;
  font-weight: 900;
  white-space: nowrap;
}
.live-tag {
  background: rgba(225, 74, 74, 0.08);
  color: #df4b4b;
}
.soon-tag {
  background: rgba(217, 119, 6, 0.09);
  color: #b5720a;
}
.live-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #e05252;
  animation: pulse 1.2s ease-in-out infinite;
}
@keyframes pulse {
  50% {
    opacity: 0.4;
    transform: scale(1.5);
  }
}
.match-row {
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 9px;
  background: #f3f8f6;
  border: 1px solid #ddeae5;
}
.match-row.winner {
  background: linear-gradient(90deg, rgba(2, 140, 115, 0.13), rgba(2, 140, 115, 0.05));
  border-color: rgba(2, 140, 115, 0.28);
}
.player-info {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
}
.match-nama {
  min-width: 0;
  display: block;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 700;
  color: #1a3830;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.match-row.winner .match-nama {
  color: #016653;
  font-weight: 900;
}
.winner-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #c88d22;
  background: #fff8df;
  border: 1px solid rgba(200, 141, 34, 0.15);
}
.player-divider {
  height: 1px;
  margin: 3px 8px;
  background: linear-gradient(90deg, transparent, #dfe9e6, transparent);
}
.skor-box,
.vs-dash {
  flex: 0 0 33px;
  min-width: 33px;
  height: 29px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  font-size: 14px;
  font-weight: 900;
}
.skor-box {
  color: #1a3830;
  background: #e5f0ec;
  border: 1.5px solid #c4dcd5;
}
.match-row.winner .skor-box {
  color: #016653;
  background: rgba(2, 140, 115, 0.14);
  border-color: rgba(2, 140, 115, 0.32);
}
.vs-dash {
  color: #98afaa;
  background: #f3f8f6;
  border: 1px dashed #c8dbd6;
}
.bye-tag,
.tbd-tag {
  font-size: 8px;
  font-weight: 850;
  padding: 4px 7px;
  border-radius: 6px;
}
.bye-tag {
  color: #8c6b29;
  background: #fff8e6;
  border: 1px solid #f0dfb5;
}
.tbd-tag {
  color: #8ea09c;
  background: #f1f5f4;
  border: 1px solid #dce7e4;
}
@media (max-width: 700px) {
  .esport-match-card-view {
    width: 220px;
  }
  .esport-match-card-view.small {
    width: 200px;
  }
  .esport-match-card-view.final {
    width: 240px;
  }
  .match-nama {
    font-size: 10.5px;
  }
}
@media (max-width: 480px) {
  .esport-match-card-view {
    width: 205px;
    padding: 9px;
  }
  .esport-match-card-view.small {
    width: 190px;
  }
  .esport-match-card-view.final {
    width: 225px;
  }
  .match-nama {
    font-size: 10px;
  }
}
</style>
